import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageFactoryService } from '../message-factory.service';
import { RootState } from '../../../state/state';
import { Store } from '@ngrx/store';
import {
  addMessageFailure,
  addMessageSuccess,
  addMessage,
  addMultipleJetStreamMessagesNoDuplicate,
  addJetStreamMessageComplete,
  addJetStreamMessage,
  addJetStreamShortMessage,
  addJetStreamShortMessageComplete,
  addGlobalMessage,
  addGlobalMessageFailure,
  addGlobalMessageSuccess,
  addGlobalJetStreamMessage,
  addGlobalJetStreamMessageComplete,
  addMileagePlanMessage,
  addMileagePlanMessageFailure,
  addMileagePlanMessageSuccess,
  addCarekitMessage,
  addCarekitMessageFailure,
  addCarekitMessageSuccess,
  addCouponMessage,
  addCouponMessageFailure,
  addCouponMessageSuccess,
  addInvoluntaryChangeMessage,
  addInvoluntaryChangeMessageFailure,
  addInvoluntaryChangeMessageSuccess,
  addManualAssociationMessage,
  addManualAssociationMessageSuccess,
  addManualAssociationMessageFailure,
  addPaymentMessage,
  addPaymentMessageFailure,
  addPaymentMessageSuccess,
  addTcpMessage,
  addTcpMessageFailure,
  addTcpMessageSuccess,
  addSdcMessage,
  addSdcMessageFailure,
  addSdcMessageSuccess,
  addSsrMessage,
  addSsrMessageFailure,
  addSsrMessageSuccess,
  addSeatmapMessage,
  addSeatmapMessageFailure,
  addSeatmapMessageSuccess,
  addReservationLongMessage,
  addReservationMessageFailure,
  addReservationMessageSuccess,
  addJetStreamMessageNoDuplicates,
  addJetStreamMessageNoDuplicatesComplete,
} from './message.actions';
import { exhaustMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageStatus } from '../../../models/message/message-status';
import { MessageSelectors } from './message.selectors';
import { RichMessage } from '../../../models/message/rich-message';

@Injectable()
export class MessageEffects {
  private readonly actions$ = inject(Actions);
  private readonly messageFactoryService = inject(MessageFactoryService);
  private readonly store = inject<Store<RootState>>(Store);

  addGlobalMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addGlobalMessage),
      exhaustMap((action) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addGlobalMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }
        return of(addGlobalMessageSuccess(message));
      })
    )
  );

  addMileagePlanMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addMileagePlanMessage),
      withLatestFrom(this.store.select(MessageSelectors.getMileagePlanMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addMileagePlanMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addMileagePlanMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addMileagePlanMessageSuccess(message));
      })
    )
  );

  addCarekitMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCarekitMessage),
      withLatestFrom(this.store.select(MessageSelectors.getCarekitLongMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addCarekitMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addCarekitMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addCarekitMessageSuccess(message));
      })
    )
  );

  addCouponMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCouponMessage),
      withLatestFrom(this.store.select(MessageSelectors.getCouponMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addCouponMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addCouponMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addCouponMessageSuccess(message));
      })
    )
  );

  addInvoluntaryChangeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addInvoluntaryChangeMessage),
      withLatestFrom(this.store.select(MessageSelectors.getIrropMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs, action.messageDetails);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addInvoluntaryChangeMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addInvoluntaryChangeMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addInvoluntaryChangeMessageSuccess(message));
      })
    )
  );

  addManualAssociationMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addManualAssociationMessage),
      withLatestFrom(this.store.select(MessageSelectors.getManualAssociationMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs, action.messageDetails);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addManualAssociationMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addManualAssociationMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addManualAssociationMessageSuccess(message));
      })
    )
  );

  addPaymentMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addPaymentMessage),
      withLatestFrom(this.store.select(MessageSelectors.getPaymentMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs, action.messageDetails);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addPaymentMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addPaymentMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addPaymentMessageSuccess(message));
      })
    )
  );

  addTcpMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addTcpMessage),
      withLatestFrom(this.store.select(MessageSelectors.getTcpMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addTcpMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addTcpMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addTcpMessageSuccess(message));
      })
    )
  );

  addSdcMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addSdcMessage),
      withLatestFrom(this.store.select(MessageSelectors.getSdcMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addSdcMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addSdcMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addSdcMessageSuccess(message));
      })
    )
  );

  addSsrMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addSsrMessage),
      withLatestFrom(this.store.select(MessageSelectors.getSsrMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addSsrMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addSsrMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addSsrMessageSuccess(message));
      })
    )
  );

  addSeatmapMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addSeatmapMessage),
      withLatestFrom(this.store.select(MessageSelectors.getSeatmapMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addSeatmapMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addSeatmapMessageFailure(MessageStatus.DUPLICATE));
        }

        return of(addSeatmapMessageSuccess(message));
      })
    )
  );

  addReservationMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addReservationLongMessage),
      withLatestFrom(this.store.select(MessageSelectors.getReservationMessages)),
      exhaustMap(([action, messages]) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        const isDuplicateMessage = messages.some((m) => m.key === message.key);

        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addReservationMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }

        if (isDuplicateMessage) {
          return of(addReservationMessageFailure(MessageStatus.DUPLICATE));
        }

        // Retain the original args so the stacked-message-bar can redisplay this message via messagesWithArgs
        // without losing any injected data (e.g. placeholders like {0}).
        message.messageArgs = action.messageArgs;

        return of(addReservationMessageSuccess(message));
      })
    )
  );

  addMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addMessage),
      exhaustMap((action) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addMessageFailure(MessageStatus.UNHANDLED_MESSAGE));
        }
        return of(addMessageSuccess(message));
      })
    )
  );

  addMultipleJetStreamMessagesNoDuplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addMultipleJetStreamMessagesNoDuplicate),
      exhaustMap((action) => {
        const messagesToAdd: RichMessage[] = [];
        const uniqueKeys = [...new Set(action.keys)];
        uniqueKeys.forEach((key) => {
          const message = this.messageFactoryService.getRichMessage(key, action.messageArgs);
          if (message) {
            message.passengerId = action.messageFilters?.passengerId;
            messagesToAdd.push(message);
          }
        });
        return of(addJetStreamMessageNoDuplicatesComplete(messagesToAdd));
      })
    )
  );

  addGlobalJetStreamMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addGlobalJetStreamMessage),
      exhaustMap((action) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addGlobalJetStreamMessageComplete([]));
        }
        return of(addGlobalJetStreamMessageComplete([message]));
      })
    )
  );

  addJetStreamMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addJetStreamMessage),
      exhaustMap((action) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addJetStreamMessageComplete([]));
        }
        return of(addJetStreamMessageComplete([message]));
      })
    )
  );

  addJetStreamMessageNoDuplicates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addJetStreamMessageNoDuplicates),
      exhaustMap((action) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs);
        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addJetStreamMessageNoDuplicatesComplete([]));
        }
        return of(addJetStreamMessageNoDuplicatesComplete([message]));
      })
    )
  );

  addJetStreamShortMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addJetStreamShortMessage),
      exhaustMap((action) => {
        const message = this.messageFactoryService.getRichMessage(action.key, action.messageArgs, action.messageDetails);
        if (!message || (!message.mainText && !message.type && !message.priority)) {
          return of(addJetStreamShortMessageComplete([]));
        }
        return of(addJetStreamShortMessageComplete([message]));
      })
    )
  );
}
